/**
 * Thin axios wrapper for the marketing "Request access" leads / simple CRM.
 *
 * The PUBLIC create endpoint (`POST /access-requests`) is called by the
 * marketing site, not this app. Here we only need the ADMIN read/manage side:
 *   GET   /admin/access-requests        — list / filter the pipeline
 *   PATCH /admin/access-requests/{id}   — update status + notes
 *
 * ADMIN role is enforced server-side. Mirrors `src/api/dataChanges.ts`.
 */
import client from '@/api/client';
import type { ListEnvelope } from '@/api/dataChanges';

export type LeadPersona = 'campaign' | 'committee';
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'closed' | 'spam';

export const LEAD_STATUSES: LeadStatus[] = [
  'new',
  'contacted',
  'qualified',
  'closed',
  'spam',
];

export interface AccessRequest {
  id: number;
  created_at: string;
  name: string;
  email: string;
  organization: string | null;
  persona: LeadPersona;
  message: string | null;
  source: string | null;
  referrer: string | null;
  ip: string | null;
  user_agent: string | null;
  status: LeadStatus;
  notes: string | null;
  contacted_at: string | null;
}

export type LeadSortKey = 'created_at' | 'status' | 'persona';

export interface FetchLeadsOptions {
  q?: string;
  status?: LeadStatus;
  persona?: LeadPersona;
  sort?: LeadSortKey;
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function buildParams(opts: FetchLeadsOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: opts.page ?? 1,
    page_size: opts.pageSize ?? 50,
  };
  if (opts.q && opts.q.trim()) params.q = opts.q.trim();
  if (opts.status) params.status = opts.status;
  if (opts.persona) params.persona = opts.persona;
  if (opts.sort) params.sort = opts.sort;
  if (opts.order) params.order = opts.order;
  return params;
}

export async function fetchAccessRequests(
  opts: FetchLeadsOptions = {},
): Promise<ListEnvelope<AccessRequest>> {
  const res = await client.get<ListEnvelope<AccessRequest>>(
    '/admin/access-requests',
    { params: buildParams(opts) },
  );
  return res.data;
}

export async function updateAccessRequest(
  id: number,
  patch: { status?: LeadStatus; notes?: string },
): Promise<AccessRequest> {
  const res = await client.patch<AccessRequest>(
    `/admin/access-requests/${id}`,
    patch,
  );
  return res.data;
}
